import { searchRoot, serializeSearchRoot } from './searchFormatting';

// Quote and apostrophe styles offered by the Replace quotes and Replace apostrophes tabs.
export const DOUBLE_STYLES = [
	{ id: 'curly', sample: '“quotes”', languages: 'English', group: 'Common', open: '“', close: '”' },
	{ id: 'guillemets', sample: '«quotes»', languages: 'French, Spanish, Greek, Italian, Russian', group: 'Common', open: '«', close: '»' },
	{ id: 'straight', sample: '"quotes"', languages: 'Typewriter style', group: 'Common', open: '"', close: '"' },
	{ id: 'german', sample: '„quotes“', languages: 'German, Czech, Slovak', group: 'Other languages', open: '„', close: '“' },
	{ id: 'polish', sample: '„quotes”', languages: 'Polish, Romanian, Hungarian', group: 'Other languages', open: '„', close: '”' },
	{ id: 'swedish', sample: '”quotes”', languages: 'Swedish, Finnish', group: 'Other languages', open: '”', close: '”' },
	{ id: 'reversed', sample: '»quotes«', languages: 'Danish', group: 'Other languages', open: '»', close: '«' },
];

export const SINGLE_STYLES = [
	{ id: 'curly', sample: '‘quotes’', languages: 'English', group: 'Common', open: '‘', close: '’' },
	{ id: 'straight', sample: "'quotes'", languages: 'Typewriter style', group: 'Common', open: "'", close: "'" },
	{ id: 'german', sample: '‚quotes‘', languages: 'German, Czech, Slovak', group: 'Other languages', open: '‚', close: '‘' },
	{ id: 'guillemets', sample: '‹quotes›', languages: 'French, Swiss', group: 'Other languages', open: '‹', close: '›' },
];

export const APOSTROPHE_STYLES = [
	{ id: 'typographic', sample: 'l’exemple', languages: 'Typographic apostrophe', group: 'Common', char: '’' },
	{ id: 'straight', sample: "l'exemple", languages: 'Straight apostrophe', group: 'Common', char: "'" },
	{ id: 'modifier', sample: 'lʼexemple', languages: 'Modifier letter apostrophe', group: 'Other languages', char: 'ʼ' },
];

const STYLES = { double: DOUBLE_STYLES, single: SINGLE_STYLES, apostrophe: APOSTROPHE_STYLES };

export const getQuoteStyle = (kind, id) => (STYLES[kind] || []).find((style) => style.id === id);

const DOUBLE_CHARS = new Set(['"', '“', '”', '„', '‟', '«', '»']);
const SINGLE_CHARS = new Set(["'", '’', '‘', '‚', '‹', '›', 'ʼ']);
export const HAS_QUOTE_CHARS = /["“”„‟«»'’‘‚‹›ʼ]/;

// Built from strings because the Next.js Babel setup cannot compile \p{...} literals.
const LETTER = new RegExp('[\\p{L}\\p{M}]', 'u');
const DIGIT = /\d/;
const BOUNDARY_BEFORE = /[\s\u00a0\u202f(\[{\u2014\u2013\/-]/;
const BOUNDARY_AFTER = /[\s\u00a0\u202f)\]}.,;:!?\u2026\u2014\u2013\/-]/;
const PADDING = /[\u00a0\u202f]/;

const boundaryBefore = (ch) => ch === undefined || BOUNDARY_BEFORE.test(ch);
const boundaryAfter = (ch) => ch === undefined || BOUNDARY_AFTER.test(ch);
const isGuillemet = (ch) => ch === '«' || ch === '»';

// Works out whether every double quote mark opens or closes a quotation from its
// surroundings, so a stray quote cannot flip the roles of the rest of the segment.
export const classifyDouble = (text) => {
	const marks = [];
	let depth = 0;

	for (let index = 0; index < text.length; index++) {
		const char = text[index];
		if (!DOUBLE_CHARS.has(char)) continue;

		const prev = text[index - 1];
		const next = text[index + 1];
		// 5" is an inch mark.
		if (char === '"' && prev !== undefined && DIGIT.test(prev) && boundaryAfter(next)) continue;

		const before = boundaryBefore(prev);
		const after = boundaryAfter(next);
		let role;
		if (char === '„' || char === '‟') role = 'open';
		else if (char === '«') role = !before && after ? 'close' : 'open';
		else if (char === '»') role = before && !after ? 'open' : 'close';
		else if (before && !after) role = 'open';
		else if (!before && after) role = 'close';
		else role = depth > 0 ? 'close' : 'open';

		depth = role === 'open' ? depth + 1 : Math.max(0, depth - 1);
		marks.push({ index, char, role });
	}

	return marks;
};

const pairMarks = (marks) => {
	const stack = [];
	const pairs = [];
	const unmatched = [];
	marks.forEach((mark) => {
		if (mark.role === 'open') stack.push(mark);
		else if (stack.length) pairs.push([stack.pop(), mark]);
		else unmatched.push(mark);
	});
	return { pairs, unmatched: unmatched.concat(stack) };
};

const styleOfPair = (styles, open, close) =>
	(styles.find((style) => style.open === open.char && style.close === close.char) || { id: 'other' }).id;

// Separates apostrophes (l'exemple, dogs') from single quotation marks ('hello').
export const classifySingle = (text) => {
	const apostrophes = [];
	const quotes = [];
	const stack = [];

	for (let index = 0; index < text.length; index++) {
		const char = text[index];
		if (!SINGLE_CHARS.has(char)) continue;

		const prev = text[index - 1];
		const next = text[index + 1];
		// 5'10 is a foot mark.
		if (char === "'" && prev !== undefined && DIGIT.test(prev) && (next === undefined || DIGIT.test(next) || boundaryAfter(next))) continue;

		if (char === 'ʼ') {
			apostrophes.push({ index, char });
		} else if (char === '‹' || char === '‚') {
			stack.push({ index, char, forced: true });
		} else if (char === '›') {
			if (stack.length) quotes.push([stack.pop(), { index, char }]);
			else quotes.push([null, { index, char }]);
		} else if (prev !== undefined && next !== undefined && LETTER.test(prev) && LETTER.test(next)) {
			apostrophes.push({ index, char });
		} else {
			const before = boundaryBefore(prev);
			const after = boundaryAfter(next);
			if (before && !after) {
				stack.push({ index, char });
			} else if (!before && after && stack.length) {
				quotes.push([stack.pop(), { index, char }]);
			} else {
				apostrophes.push({ index, char });
			}
		}
	}

	// An opening mark that never closes is an elision ('90s) unless it can only be a quote.
	stack.forEach((mark) => {
		if (mark.forced) quotes.push([mark, null]);
		else apostrophes.push({ index: mark.index, char: mark.char });
	});

	return { apostrophes, quotes };
};

const replaceMarks = (marks, style, text) => {
	const map = [];
	let count = 0;

	marks.forEach(({ index, char, role }) => {
		const next = role === 'open' ? style.open : style.close;
		if (next === char) return;
		map[index] = next;
		count++;
		// French spacing belongs to the guillemets, not to other quote styles.
		if (isGuillemet(char) && !isGuillemet(next)) {
			const pad = role === 'open' ? index + 1 : index - 1;
			if (PADDING.test(text[pad] ?? '')) map[pad] = '';
		}
	});

	return { map, count };
};

const APOSTROPHE_IDS = { '’': 'typographic', "'": 'straight', 'ʼ': 'modifier' };

// Returns a per-character replacement map, so the result can be written back into text nodes.
// fromId limits the conversion to one source style; without it every style is converted.
export const convertText = (text, kind, style, fromId = '') => {
	if (kind === 'double') {
		let marks = classifyDouble(text);
		if (fromId) {
			marks = pairMarks(marks).pairs
				.filter(([open, close]) => styleOfPair(DOUBLE_STYLES, open, close) === fromId)
				.flat();
		}
		return replaceMarks(marks, style, text);
	}

	const { apostrophes, quotes } = classifySingle(text);
	if (kind === 'apostrophe') {
		const map = [];
		let count = 0;
		apostrophes.forEach(({ index, char }) => {
			if (char === style.char || (fromId && APOSTROPHE_IDS[char] !== fromId)) return;
			map[index] = style.char;
			count++;
		});
		return { map, count };
	}

	const marks = [];
	quotes.forEach(([open, close]) => {
		if (fromId && !(open && close && styleOfPair(SINGLE_STYLES, open, close) === fromId)) return;
		if (open) marks.push({ ...open, role: 'open' });
		if (close) marks.push({ ...close, role: 'close' });
	});
	return replaceMarks(marks, style, text);
};

// Writes the conversion into the segment's text nodes only, so tags and attributes stay untouched.
export const convertHtml = (html, kind, style, fromId = '') => {
	const root = searchRoot(html);
	const nodes = [];
	let text = '';
	const visit = (node) => {
		if (node.nodeType === 3) {
			nodes.push({ node, start: text.length, end: text.length + node.nodeValue.length });
			text += node.nodeValue;
		} else if (node.nodeType === 1 && !node.matches('script, style, .xlftag, [contenteditable="false"]')) {
			node.childNodes.forEach(visit);
		}
	};
	visit(root);

	const { map, count } = convertText(text, kind, style, fromId);
	if (!count) return { html, count: 0 };

	nodes.forEach(({ node, start, end }) => {
		let value = '';
		for (let i = start; i < end; i++) value += map[i] ?? text[i];
		node.nodeValue = value;
	});

	return { html: serializeSearchRoot(root), count };
};

export const toPlainText = (html) =>
	String(html ?? '')
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/g, '\u00a0')
		.replace(/&quot;/g, '"')
		.replace(/&#39;|&apos;/g, "'")
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&amp;/g, '&');

// Scans the target text of a task once and keeps what the Replace tabs need.
export const analyzeQuotes = (segments) => {
	const result = {
		entries: [],
		double: { counts: {}, unbalanced: 0, segments: {} },
		single: { counts: {}, unbalanced: 0, segments: {} },
		apostrophes: { counts: {}, segments: {} },
		issues: {},
	};
	const addTo = (bucket, id, segment, amount) => {
		bucket.counts[id] = (bucket.counts[id] || 0) + amount;
		(bucket.segments[id] = bucket.segments[id] || []).push(segment);
	};
	const flag = (id, segment) => (result.issues[id] = result.issues[id] || []).push(segment);

	segments.forEach((S) => {
		const text = toPlainText(S.translationText);
		if (!text || !HAS_QUOTE_CHARS.test(text)) return;
		const segment = { id: S.id, ordr: S.ordr };
		result.entries.push({ ...segment, text, editable: !(S.isLocked || S.isRevisionLocked || !S.available) });

		const double = pairMarks(classifyDouble(text));
		const doubleStyles = new Set();
		double.pairs.forEach(([open, close]) => {
			const id = styleOfPair(DOUBLE_STYLES, open, close);
			doubleStyles.add(id);
			addTo(result.double, id, segment, 2);
		});
		if (double.unmatched.length) {
			result.double.unbalanced += double.unmatched.length;
			flag('unbalancedDouble', segment);
		}
		if (doubleStyles.size > 1) flag('mixedDouble', segment);

		const { apostrophes, quotes } = classifySingle(text);
		quotes.forEach(([open, close]) => {
			if (!open || !close) {
				result.single.unbalanced++;
				return;
			}
			addTo(result.single, styleOfPair(SINGLE_STYLES, open, close), segment, 2);
		});

		const apostropheChars = new Set();
		apostrophes.forEach(({ char }) => {
			apostropheChars.add(char);
			addTo(result.apostrophes, APOSTROPHE_IDS[char] || 'other', segment, 1);
		});
		if (apostropheChars.size > 1) flag('mixedApostrophes', segment);
	});

	const typographic = Object.keys(result.double.counts).some((id) => id !== 'straight');
	if (typographic) (result.double.segments.straight || []).forEach((segment) => flag('straightAmongTypographic', segment));

	const quoteStyles = [
		...Object.keys(result.double.counts).map((id) => ({ bucket: result.double, id })),
		...Object.keys(result.single.counts).map((id) => ({ bucket: result.single, id })),
	].sort((a, b) => b.bucket.counts[b.id] - a.bucket.counts[a.id]);
	quoteStyles.slice(1).forEach(({ bucket, id }) => bucket.segments[id].forEach((segment) => flag('minorityQuote', segment)));

	const apostropheIds = Object.keys(result.apostrophes.counts);
	if (apostropheIds.length > 1) {
		const main = apostropheIds.sort((a, b) => result.apostrophes.counts[b] - result.apostrophes.counts[a])[0];
		apostropheIds.filter((id) => id !== main)
			.forEach((id) => result.apostrophes.segments[id].forEach((segment) => flag('minorityApostrophe', segment)));
	}

	return result;
};

export const ISSUE_LABELS = {
	mixedDouble: 'Mixed double quote styles in one segment',
	unbalancedDouble: 'Double quotes that are opened but not closed, or the reverse',
	minorityQuote: 'Quotes that differ from the most used style',
	straightAmongTypographic: 'Straight double quotes among typographic ones',
	mixedApostrophes: 'Different apostrophes in the same segment',
	minorityApostrophe: 'Apostrophes that differ from the most used style',
};

export const uniqueSegments = (list = []) => {
	const seen = new Set();
	return list.filter(({ id }) => !seen.has(id) && seen.add(id));
};

// Locked segments are skipped when replacing, so they are not counted either.
export const countChanges = (analysis, kind, style, fromId = '') =>
	analysis.entries.reduce((total, { text, editable }) => total + (editable ? convertText(text, kind, style, fromId).count : 0), 0);

// A real before/after sample from the task for the style the user picked.
export const findExample = (analysis, kind, style, fromId = '') => {
	for (const { ordr, text, editable } of analysis.entries) {
		if (!editable) continue;
		const { map, count } = convertText(text, kind, style, fromId);
		if (!count) continue;

		const first = map.findIndex((value) => value !== undefined);
		const from = Math.max(0, first - 28);
		const to = Math.min(text.length, first + 40);
		let after = '';
		for (let i = from; i < to; i++) after += map[i] ?? text[i];
		const ellipsis = (start, end) => [start > 0 ? '…' : '', end < text.length ? '…' : ''];
		const [head, tail] = ellipsis(from, to);

		return { ordr, before: head + text.slice(from, to) + tail, after: head + after + tail };
	}
	return null;
};
